<template>
  <div>
    <div class="relative" :class="[type === 'categories' ? 'z-40' : 'z-30']">
      <div class="relative flex items-center">
        <Search
          :size="20"
          :stroke-width="1.75"
          class="absolute left-4 opacity-40"
        />
        <AppInput
          :mode="mode"
          type="search"
          :placeholder="`Search by ${type === 'categories' ? 'product category' : 'specialty'} name`"
          v-model="searchQuery"
          classes="!pl-11"
          @focus="searchFocus = true"
          :hasLabel="false"
        />
      </div>
      <div
        v-if="searchFocus"
        class="without-scrollbar absolute left-0 right-0 top-full z-10 mt-1 max-h-[210px] overflow-y-auto rounded-lg border p-4"
        :class="[
          mode === 'dark'
            ? 'border-dark-border bg-dark-sidebar'
            : 'border-black-10 bg-white-100',
        ]"
      >
        <div v-if="filteredData && filteredData.length > 0" class="grid gap-3">
          <AppCheckbox
            v-for="(item, key) in filteredData"
            :key="key"
            :mode="mode"
            :id="item.id"
            :value="
              type === 'categories' ? item.category_name : item.specialty_name
            "
            v-model="selectedData"
            :title="
              type === 'categories' ? item.category_name : item.specialty_name
            "
          />
        </div>
        <div v-else class="text-center">
          <span
            class="text-sm font-medium"
            :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-80']"
            >No
            {{ type === "categories" ? "product categories" : "specialties" }}
            found to select</span
          >
        </div>
      </div>
    </div>
    <div
      class="fixed inset-0 z-10"
      v-if="searchFocus"
      @click="searchFocus = false"
    ></div>
    <div v-if="selectedData.length > 0" class="mt-4">
      <div class="flex flex-wrap items-center gap-2">
        <span
          class="cursor-pointer rounded-md border border-primary-100 px-3 py-1 text-[13px] font-medium text-primary-100 transition-colors hover:bg-primary-100 hover:text-white-100"
          :class="[mode === 'dark' ? 'bg-dark-sidebar' : 'bg-white-100']"
          v-for="(item, index) in selectedData"
          @click="removeSelectedItem(item)"
          :key="index"
        >
          {{ item }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { Search } from "lucide-vue-next";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useGlobalErrorHandler } from "@L/errorHandler";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppInput from "@C/Form/AppInput.vue";

export default {
  name: "CategoriesSpecialties",
  props: {
    mode: String,
    data: {
      type: Array,
      default: [],
    },
    type: {
      type: String,
      default: "categories",
    },
    set: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      handleError,
    };
  },
  components: { Search, AppCheckbox, AppInput },
  data() {
    return {
      searchQuery: "",
      searchFocus: false,
      fetchedData: [],
      selectedData: [],
    };
  },
  computed: {
    filteredData() {
      if (!this.fetchedData || !this.fetchedData.length) return [];
      const query = this.searchQuery.toLowerCase();
      return this.fetchedData.filter((item) => {
        if (this.type === "categories") {
          return item.category_name.toLowerCase().includes(query);
        } else {
          return item.specialty_name.toLowerCase().includes(query);
        }
      });
    },
  },
  methods: {
    async getData() {
      try {
        const response = await fetch(
          `/public_api/${this.type === "categories" ? "product_categories" : "specialties"}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (!response.ok) {
          this.toastStore.show(
            `Failed to fetch ${this.type === "categories" ? "product categories" : "specialties"}`,
            "error",
          );
          return;
        }
        const data = await response.json();
        if (!data) {
          this.toastStore.show(
            `No ${this.type === "categories" ? "product categories" : "specialties"} found`,
            "error",
          );
          return;
        }
        this.fetchedData = data;
      } catch (e) {
        this.handleError(e);
      }
    },
    removeSelectedItem(item) {
      const index = this.selectedData.findIndex((i) => i === item);
      if (index > -1) this.selectedData.splice(index, 1);
    },
  },
  mounted() {
    this.getData();
  },
  watch: {
    set: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          if (Array.isArray(this.data)) {
            this.selectedData = [...this.data];
          }
        }
      },
    },
    selectedData: {
      deep: true,
      handler(newValue) {
        this.$emit("selectedDataUpdated", newValue);
      },
    },
  },
};
</script>
